import { useEffect, useRef, useState } from 'react';
import { useAuth, useSignIn } from '@clerk/clerk-react';
import { LoadingState } from '@bcecloud/portal-ui';

/**
 * The fragment key a Salesforce one-time link lands with (the API's
 * `CLERK_TICKET_FRAGMENT`): a staff Clerk sign-in ticket, minted for the rep
 * who clicked "Open in BCE Cloud". It rides the fragment so it never reaches a
 * server log or a Referer.
 */
export const CLERK_TICKET_FRAGMENT = '__clerk_ticket';

/**
 * Reads the ticket from the URL fragment and removes it, so it cannot be
 * bookmarked, shared or replayed from history. Null when there is none.
 *
 * @param location - The window location (injected for tests).
 * @param history - The window history (injected for tests).
 * @returns The ticket, or null.
 */
export function takeTicketFromFragment(location: Location, history: History): string | null {
  const raw = location.hash.startsWith('#') ? location.hash.slice(1) : '';
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const ticket = params.get(CLERK_TICKET_FRAGMENT);
  if (!ticket) return null;
  params.delete(CLERK_TICKET_FRAGMENT);
  const rest = params.toString();
  history.replaceState(history.state, '', `${location.pathname}${location.search}${rest ? `#${rest}` : ''}`);
  return ticket;
}

const TICKET_STATE = { none: 'none', signingIn: 'signing-in', done: 'done' } as const;
type TicketState = (typeof TICKET_STATE)[keyof typeof TICKET_STATE];

export interface ClerkTicketSignInProps {
  readonly children: React.ReactNode;
}

/**
 * Signs a rep in from a Salesforce one-time link before the sign-in gate
 * renders: when the URL carries a ticket and nobody is signed in, it redeems
 * the ticket for a normal staff Clerk session. Someone already signed in keeps
 * their session (the ticket is simply dropped). A ticket that fails — used,
 * expired — falls through to the ordinary sign-in screen, and the rep still
 * lands on the proposal once signed in, because the URL is unchanged.
 *
 * The effect syncs with an external system (Clerk), which is what effects are
 * for; the ref keeps StrictMode's double run from spending the single-use
 * ticket twice.
 *
 * @param props - The gated tree.
 * @returns The tree, or a loading state while signing in.
 */
export function ClerkTicketSignIn({ children }: ClerkTicketSignInProps): React.ReactElement {
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded: signInLoaded, signIn, setActive } = useSignIn();
  const [ticket] = useState(() => takeTicketFromFragment(window.location, window.history));
  const [state, setState] = useState<TicketState>(ticket ? TICKET_STATE.signingIn : TICKET_STATE.none);
  const started = useRef(false);

  useEffect(() => {
    if (!ticket || started.current || !authLoaded || !signInLoaded) return;
    started.current = true;
    if (isSignedIn) {
      setState(TICKET_STATE.done);
      return;
    }
    signIn
      .create({ strategy: 'ticket', ticket })
      .then(async (attempt) => {
        if (attempt.status === 'complete' && attempt.createdSessionId) {
          await setActive({ session: attempt.createdSessionId });
        }
      })
      .catch(() => undefined)
      .finally(() => setState(TICKET_STATE.done));
  }, [ticket, authLoaded, signInLoaded, isSignedIn, signIn, setActive]);

  if (state === TICKET_STATE.signingIn) {
    return (
      <main className="grid min-h-screen place-items-center bg-portal-bg">
        <LoadingState label="Signing you in…" />
      </main>
    );
  }
  return <>{children}</>;
}
